import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import '../styles/SearchModal.css';

export interface SearchResult {
  category: 'pages' | 'books' | 'sanskrit' | 'work' | 'people';
  title: string;
  subtitle?: string;
  snippet?: string;
  path: string;
  auth_required?: boolean;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
  token?: string | null;
  initialQuery?: string;
}

const CATEGORY_META: Record<SearchResult['category'], { icon: string; label: string }> = {
  pages:   { icon: '📄', label: 'Pages' },
  books:   { icon: '📚', label: 'Books' },
  sanskrit:{ icon: '🕉️', label: 'Sanskrit' },
  work:    { icon: '💼', label: 'Work' },
  people:  { icon: '👥', label: 'People' },
};

const CATEGORY_ORDER: SearchResult['category'][] = ['pages', 'books', 'sanskrit', 'work', 'people'];

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function SearchModal({ isOpen, onClose, onNavigate, token, initialQuery = '' }: SearchModalProps) {
  const [query, setQuery]       = useState(initialQuery);
  const [results, setResults]   = useState<SearchResult[]>([]);
  const [loading, setLoading]   = useState(false);
  const [focused, setFocused]   = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef  = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 280);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
      setFocused(-1);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [isOpen, initialQuery]);

  // Fetch search results
  useEffect(() => {
    if (!isOpen) return;
    const q = debouncedQuery.trim();
    let cancelled = false;
    setLoading(true);

    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const url = `/api/search?q=${encodeURIComponent(q)}&limit=30`;
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        if (!cancelled) {
          setResults(data.results ?? []);
          setFocused(-1);
        }
      })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [debouncedQuery, isOpen, token]);

  // Group results by category in defined order
  const grouped = useMemo(() => {
    const map = new Map<string, SearchResult[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const r of results) {
      const arr = map.get(r.category);
      if (arr) arr.push(r);
    }
    return CATEGORY_ORDER
      .map(cat => ({ cat, items: map.get(cat)! }))
      .filter(g => g.items.length > 0);
  }, [results]);

  // Flat list for keyboard navigation
  const flatItems = useMemo(() => results.filter(r => !r.auth_required || token), [results, token]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocused(f => Math.min(f + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocused(f => Math.max(f - 1, -1));
    } else if (e.key === 'Enter' && focused >= 0 && flatItems[focused]) {
      e.preventDefault();
      const item = flatItems[focused];
      if (item.auth_required && !token) {
        onNavigate('/profile');
      } else {
        onNavigate(item.path);
      }
      onClose();
    }
  }, [focused, flatItems, onClose, onNavigate, token]);

  // Scroll focused item into view
  useEffect(() => {
    if (focused >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${focused}"]`) as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [focused]);

  const handleResultClick = useCallback((item: SearchResult) => {
    if (item.auth_required && !token) {
      onNavigate('/profile');
    } else {
      onNavigate(item.path);
    }
    onClose();
  }, [onClose, onNavigate, token]);

  if (!isOpen) return null;

  // Global flat index for data-idx
  let globalIdx = 0;

  return (
    <div className="search-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="search-palette" role="dialog" aria-modal="true" aria-label="Search portal">

        {/* Search input */}
        <div className="search-input-row">
          <span className="search-icon" aria-hidden>🔍</span>
          <input
            ref={inputRef}
            className="search-input"
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, books, Sanskrit, careers…"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <span className="search-spinner" aria-hidden />}
          <button className="search-close-btn" onClick={onClose} aria-label="Close search">✕</button>
        </div>

        {/* Results */}
        <div className="search-results-area" ref={listRef}>
          {!loading && results.length === 0 && debouncedQuery.trim() && (
            <div className="search-empty">No results for "{debouncedQuery}"</div>
          )}
          {!loading && results.length === 0 && !debouncedQuery.trim() && (
            <div className="search-hint">
              <span>Type to search across books, Sanskrit, careers, and more.</span>
              <kbd>↑↓</kbd><span> navigate</span>
              <kbd>↵</kbd><span> open</span>
              <kbd>Esc</kbd><span> close</span>
            </div>
          )}

          {grouped.map(({ cat, items }) => (
            <div key={cat} className="search-category-group">
              <div className="search-category-label">
                <span className="search-cat-icon">{CATEGORY_META[cat].icon}</span>
                {CATEGORY_META[cat].label}
              </div>
              {items.map((item) => {
                const idx = globalIdx++;
                const isFocused = focused === idx;
                const locked = item.auth_required && !token;
                return (
                  <div
                    key={`${item.path}-${item.title}`}
                    className={`search-result-row${isFocused ? ' search-result-row--focused' : ''}${locked ? ' search-result-row--locked' : ''}`}
                    data-idx={idx}
                    role="option"
                    aria-selected={isFocused}
                    onMouseEnter={() => setFocused(idx)}
                    onClick={() => handleResultClick(item)}
                  >
                    <div className="search-result-main">
                      <span className="search-result-title">{item.title}</span>
                      {item.subtitle && <span className="search-result-subtitle">{item.subtitle}</span>}
                      {item.snippet && <span className="search-result-snippet">{item.snippet}</span>}
                    </div>
                    <div className="search-result-meta">
                      {locked
                        ? <span className="search-lock" title="Sign in to view">🔒</span>
                        : <span className="search-arrow">›</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
